// frontend/src/pages/ProgressPage.tsx
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, RefreshCw, Zap } from 'lucide-react'
import { useProgressSummary, useDifficultWords, useVocabularyMap } from '../hooks/useProgress'
import { useCreateLearningSession } from '../hooks/useLearning'
import { useLocale } from '../i18n/context'
import { useScreenHeader } from '../hooks/useScreenHeader'
import { useStopScroll } from '../hooks/useStopScroll'
import VocabularyMap from '../components/VocabularyMap'
import type { MasteryBreakdown } from '../types'

// ── CEFR helpers ──────────────────────────────────────────────────────────────

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const

function nextLevel(level: string): string {
  const idx = CEFR_LEVELS.indexOf(level as (typeof CEFR_LEVELS)[number])
  return idx >= 0 && idx < CEFR_LEVELS.length - 1 ? CEFR_LEVELS[idx + 1] : level
}

// ── Activity rings ────────────────────────────────────────────────────────────
// Three concentric SVG rings (Apple Watch style).
// Outer = Words (accent blue), Middle = Grammar (grayed), Inner = Use (grayed).

const SIZE = 200
const CX = SIZE / 2
const CY = SIZE / 2
const RING_OUTER = { r: 80, sw: 14 }
const RING_MID   = { r: 62, sw: 10 }
const RING_INNER = { r: 46, sw: 10 }

function circumference(r: number) {
  return 2 * Math.PI * r
}

interface RingProps {
  r: number
  sw: number
  fill: number  // 0.0 – 1.0
  color: string
  bgColor?: string
}

function Ring({ r, sw, fill, color, bgColor = 'var(--border)' }: RingProps) {
  const c = circumference(r)
  const offset = c * (1 - Math.min(Math.max(fill, 0), 1))

  return (
    <g>
      {/* Track */}
      <circle
        cx={CX} cy={CY} r={r}
        fill="none"
        stroke={bgColor}
        strokeWidth={sw}
        strokeLinecap="round"
        opacity={0.25}
      />
      {/* Fill */}
      <circle
        cx={CX} cy={CY} r={r}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${CX} ${CY})`}
        style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
      />
    </g>
  )
}

interface ActivityRingsProps {
  wordsFill: number
  /** Vocabulary-volume band (from vocab_band) — used for milestone label, not level identity */
  vocabBand: string
  nextVocabBand: string
}

function ActivityRings({ wordsFill, vocabBand, nextVocabBand }: ActivityRingsProps) {
  const { tr } = useLocale()
  const p = tr.progress
  const isC2 = vocabBand === 'C2'
  const pct = Math.round(wordsFill * 100)

  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      aria-label="Activity rings showing learning progress"
      role="img"
    >
      {/* Outer: Words */}
      <Ring r={RING_OUTER.r} sw={RING_OUTER.sw} fill={wordsFill} color="var(--accent)" />
      {/* Middle: Grammar — coming soon */}
      <Ring r={RING_MID.r} sw={RING_MID.sw} fill={0} color="var(--text-secondary)" />
      {/* Inner: Use — coming soon */}
      <Ring r={RING_INNER.r} sw={RING_INNER.sw} fill={0} color="var(--text-secondary)" />

      {/* Center text */}
      {isC2 ? (
        <>
          <text x={CX} y={CY - 6} textAnchor="middle" fontSize="15" fontWeight="700" fill="var(--text-primary)" fontFamily="var(--font-ui)">
            C2
          </text>
          <text x={CX} y={CY + 12} textAnchor="middle" fontSize="10" fill="var(--text-secondary)" fontFamily="var(--font-ui)">
            {p.c2Mastered}
          </text>
        </>
      ) : (
        <>
          <text x={CX} y={CY - 6} textAnchor="middle" fontSize="20" fontWeight="700" fill="var(--text-primary)" fontFamily="var(--font-ui)" letterSpacing="-0.02em">
            {pct}%
          </text>
          <text x={CX} y={CY + 12} textAnchor="middle" fontSize="10" fill="var(--text-secondary)" fontFamily="var(--font-ui)">
            {p.toVocabBand(nextVocabBand)}
          </text>
        </>
      )}
    </svg>
  )
}

// ── Ring legend ────────────────────────────────────────────────────────────────

interface RingLegendItemProps {
  color: string
  name: string
  fraction: string
  pct: string
  comingSoon?: boolean
}

function RingLegendItem({ color, name, fraction, pct, comingSoon }: RingLegendItemProps) {
  const { tr } = useLocale()
  return (
    <div className="flex items-center gap-3">
      {/* Color dot */}
      <div
        className="flex-shrink-0 rounded-full"
        style={{
          width: 12, height: 12,
          background: comingSoon ? 'var(--border)' : color,
          opacity: comingSoon ? 0.5 : 1,
        }}
      />
      <div style={{ opacity: comingSoon ? 0.45 : 1 }}>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {name}
          </span>
          {!comingSoon && (
            <span className="text-xs font-bold tabular-nums" style={{ color }}>
              {pct}
            </span>
          )}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {comingSoon ? tr.progress.comingSoon : fraction}
        </span>
      </div>
    </div>
  )
}

// ── Mastery breakdown ─────────────────────────────────────────────────────────

interface MasteryGroup {
  label: string
  count: number
  // Accent-family ramp: magnitude encoded by fill, not hue (Wave 4 §6).
  // No --mastery-N rainbow tokens — those are banned as data-viz category fills.
  barColor: string
  description: string
}

function MasteryBreakdownView({ breakdown }: { breakdown: MasteryBreakdown }) {
  const { tr } = useLocale()
  const p = tr.progress
  const total = breakdown.new + breakdown.fragile + breakdown.active + breakdown.mastered
  const maxCount = Math.max(breakdown.mastered, breakdown.active, breakdown.fragile, breakdown.new, 1)

  const groups: MasteryGroup[] = [
    {
      label: p.masteredLabel,
      count: breakdown.mastered,
      barColor: 'var(--accent)',
      description: p.masteredDesc,
    },
    {
      label: p.activeLabel,
      count: breakdown.active,
      barColor: 'color-mix(in srgb, var(--accent) 58%, var(--border))',
      description: p.activeDesc,
    },
    {
      label: p.fragileLabel,
      count: breakdown.fragile,
      barColor: 'color-mix(in srgb, var(--accent) 30%, var(--border))',
      description: p.fragileDesc,
    },
    {
      label: p.newLabel,
      count: breakdown.new,
      barColor: 'color-mix(in srgb, var(--accent) 12%, var(--border))',
      description: p.newDesc,
    },
  ]

  return (
    <div className="flex flex-col gap-3">
      {groups.map(({ label, count, barColor, description }) => {
        const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0
        return (
          <div key={label} className="flex items-center gap-3">
            {/* Label */}
            <div style={{ minWidth: 68 }}>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {label}
              </span>
              <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>
                ({description})
              </span>
            </div>
            {/* Bar */}
            <div
              className="flex-1 h-2 rounded-full overflow-hidden"
              style={{ background: 'var(--bg-secondary)' }}
              role="progressbar"
              aria-valuenow={count}
              aria-valuemax={maxCount}
              aria-label={`${label}: ${count} cards`}
            >
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${barWidth}%`, background: barColor }}
              />
            </div>
            {/* Count */}
            <span
              className="text-xs font-bold tabular-nums"
              style={{ minWidth: 28, textAlign: 'right', color: count > 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}
            >
              {count.toLocaleString()}
            </span>
          </div>
        )
      })}
      {total === 0 && (
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {p.startLearning}
        </p>
      )}
    </div>
  )
}

// ── Difficult words ────────────────────────────────────────────────────────────

function DifficultWordsSection() {
  const navigate = useNavigate()
  const { tr } = useLocale()
  const p = tr.progress
  const { data, isLoading } = useDifficultWords()
  const createSession = useCreateLearningSession()
  // ADR-112: Train is a practice session, and mastered words are never practised.
  // When every listed word is mastered the server has nothing to serve (422
  // NO_DUE_CARDS); say so here rather than leave the button doing nothing.
  const [nothingToPractise, setNothingToPractise] = useState(false)

  const words = data?.words ?? []

  async function handleTrain() {
    if (words.length === 0) return
    setNothingToPractise(false)
    const cardIds = words.map((w) => w.card_id)
    createSession.mutate(
      { source_type: 'custom', card_ids: cardIds },
      {
        onSuccess: (session) => {
          navigate('/session', { state: { sessionId: session.session_id } })
        },
        onError: (err) => {
          const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
          const code = detail && typeof detail === 'object' ? (detail as Record<string, unknown>).code : undefined
          if (code === 'NO_DUE_CARDS') setNothingToPractise(true)
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-9 rounded-xl animate-pulse" style={{ background: 'var(--border)' }} />
        ))}
      </div>
    )
  }

  if (words.length === 0) {
    return (
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {p.noErrors}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        {words.map((word, idx) => (
          <div
            key={word.card_id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span
                className="text-xs font-bold tabular-nums flex-shrink-0"
                style={{ color: 'var(--text-secondary)', minWidth: 16 }}
              >
                {idx + 1}
              </span>
              <span
                className="text-sm font-medium truncate"
                style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-content)' }}
              >
                {word.term}
              </span>
            </div>
            <span
              className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full tabular-nums"
              style={{
                background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
                color: 'var(--warning)',
              }}
            >
              {word.error_count}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={handleTrain}
        disabled={createSession.isPending}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-full font-semibold text-sm transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
      >
        <Zap size={15} strokeWidth={2.5} />
        {createSession.isPending ? p.starting : p.trainWords(words.length)}
      </button>
      {nothingToPractise && (
        <p role="status" className="text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
          {p.trainNothingToPractise}
        </p>
      )}
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function ProgressPageSkeleton() {
  return (
    <div className="flex flex-col gap-10 py-10 w-full">
      {/* Rings skeleton */}
      <div className="flex flex-col sm:flex-row gap-8 items-center">
        <div className="rounded-full animate-pulse flex-shrink-0" style={{ width: SIZE, height: SIZE, background: 'var(--border)' }} />
        <div className="flex flex-col gap-4 flex-1 w-full">
          {[70, 50, 55].map((w, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="rounded-full animate-pulse" style={{ width: 12, height: 12, background: 'var(--border)' }} />
              <div className="rounded animate-pulse" style={{ width: `${w}%`, height: 14, background: 'var(--border)' }} />
            </div>
          ))}
        </div>
      </div>
      {/* Content skeletons */}
      {[3, 4, 5].map((lines, sectionIdx) => (
        <div key={sectionIdx} className="flex flex-col gap-3">
          <div className="h-4 w-28 rounded animate-pulse" style={{ background: 'var(--border)' }} />
          {Array.from({ length: lines }).map((_, i) => (
            <div key={i} className="h-8 rounded-xl animate-pulse" style={{ background: 'var(--border)' }} />
          ))}
        </div>
      ))}
    </div>
  )
}

// ── Error state ───────────────────────────────────────────────────────────────

function ProgressPageError({ onRetry }: { onRetry: () => void }) {
  const { tr } = useLocale()
  const p = tr.progress
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 px-4 text-center">
      <div
        className="flex items-center justify-center w-14 h-14 rounded-2xl"
        style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}
      >
        <AlertCircle size={26} />
      </div>
      <div>
        <h2 className="text-xl font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>
          {p.loadError}
        </h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {p.loadErrorSub}
        </p>
      </div>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-opacity hover:opacity-80"
        style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
      >
        <RefreshCw size={14} />
        {p.tryAgain}
      </button>
    </div>
  )
}

// ── Section wrapper ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <h2
        className="text-xs font-semibold uppercase tracking-widest"
        style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

// ── Stats grid ─────────────────────────────────────────────────────────────────

interface StatTileProps {
  label: string
  value: string | number
  sub?: string
  accent?: boolean
}

function StatTile({ label, value, sub, accent }: StatTileProps) {
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 4,
        padding: '16px 20px', borderRadius: 16,
        background: accent ? 'var(--accent-soft)' : 'var(--bg-surface)',
        border: `1.5px solid ${accent ? 'transparent' : 'var(--border)'}`,
        flex: 1, minWidth: 0,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: accent ? 'var(--accent)' : 'var(--text-secondary)' }}>
        {label}
      </span>
      <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: accent ? 'var(--accent)' : 'var(--text-primary)', lineHeight: 1.1 }}>
        {value}
      </span>
      {sub && (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sub}</span>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProgressPage() {
  const { tr } = useLocale()
  const p = tr.progress
  const { data: summary, isLoading, isError, refetch } = useProgressSummary()
  // The vocabulary map and the difficult-words list load on their own inside
  // the page; subscribing here (TanStack shares the one query) lets the scroll
  // restore below wait for them instead of clamping against a shorter page.
  const { isLoading: vocabMapLoading } = useVocabularyMap()
  const { isLoading: difficultLoading } = useDifficultWords()

  useScreenHeader(useMemo(() => ({ title: p.title }), [p.title]))

  // ADR-106 P4: Progress keeps its scroll position for the session.
  useStopScroll('progress', { ready: !isLoading && !vocabMapLoading && !difficultLoading })

  if (isLoading) return <ProgressPageSkeleton />
  if (isError || !summary) return <ProgressPageError onRetry={() => void refetch()} />

  const level = summary.current_level
  const vocabBand = summary.vocab_band ?? summary.current_level
  const nextVocabBand = nextLevel(vocabBand)
  const wordsFill = summary.level_progress / 100
  const breakdown = summary.mastery_breakdown ?? { new: 0, fragile: 0, active: 0, mastered: 0 }

  // Words ring legend fraction — vocabulary band progress, not level identity
  const wordsKnown = summary.level_words_known ?? 0
  const wordsTotal = summary.level_words_total ?? null
  const wordsFraction = wordsTotal != null
    ? `${wordsKnown.toLocaleString()} / ${wordsTotal.toLocaleString()} · ${vocabBand}`
    : `${wordsKnown.toLocaleString()} · ${vocabBand}`
  const wordsPct = `${Math.round(summary.level_progress)}%`

  const dailyGoal = summary.daily_card_goal ?? 0
  const dailyDone = summary.today_resolutions ?? 0
  const dailyFraction = dailyGoal > 0 ? p.todayFraction(dailyDone, dailyGoal) : p.todayCount(dailyDone)

  return (
    // No max-w-2xl/mx-auto of its own — Layout picks this route's page
    // container width (var(--page-max-width-narrow), since Progress reads
    // top-to-bottom like Library and Home) and already caps and centers it.
    // `px-4` dropped for the same reason: desktop's inset comes solely from
    // Layout's own padding; it survives on mobile via Layout's own
    // `max-md:!px-4`.
    <div
      className="flex flex-col gap-10 py-10 w-full"
      style={{ color: 'var(--text-primary)' }}
    >
      {/* Page title — mobile only; desktop's identity lives in the
          screen-header bar (see useScreenHeader above), never both. */}
      <h1
        className="text-2xl font-bold md:hidden"
        style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
      >
        {p.title}
      </h1>

      {/* ── Stat tiles: words known, streak, daily ── */}
      <section style={{ display: 'flex', gap: 12 }}>
        <StatTile
          label={p.wordsKnown}
          value={summary.words_known_count.toLocaleString()}
          sub={p.level(level)}
          accent
        />
        <StatTile
          label={p.streak}
          value={`${summary.streak_days}d`}
          sub={summary.best_streak > summary.streak_days ? p.best(summary.best_streak) : p.personalBest}
        />
        <StatTile
          label={p.today}
          value={dailyDone}
          sub={dailyFraction}
        />
      </section>

      {/* ── Rings + legend ── */}
      <section>
        <div className="flex flex-col sm:flex-row gap-8 items-center">
          {/* Rings */}
          <div className="flex-shrink-0">
            <ActivityRings wordsFill={wordsFill} vocabBand={vocabBand} nextVocabBand={nextVocabBand} />
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-5">
            <RingLegendItem
              color="var(--accent)"
              name={p.words}
              fraction={wordsFraction}
              pct={wordsPct}
            />
            <RingLegendItem
              color="var(--text-secondary)"
              name={p.grammar}
              fraction=""
              pct=""
              comingSoon
            />
            <RingLegendItem
              color="var(--text-secondary)"
              name={p.use}
              fraction=""
              pct=""
              comingSoon
            />
          </div>
        </div>
      </section>

      {/* ── Vocabulary map ── */}
      <Section title={p.vocabMap}>
        <VocabularyMap />
      </Section>

      {/* ── Mastery breakdown ── */}
      <Section title={p.mastery}>
        <div
          className="p-5 rounded-2xl"
          style={{ background: 'var(--bg-surface)', border: '1.5px solid var(--border)' }}
        >
          <MasteryBreakdownView breakdown={breakdown} />
        </div>
      </Section>

      {/* ── Difficult words ── */}
      <Section title={p.difficultWords}>
        <DifficultWordsSection />
      </Section>
    </div>
  )
}
